import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  ChevronDown,
  FileText,
  ReceiptText,
  UserPlus,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { useMemo } from 'react';
import { MenuLink } from '@/components/menu-link';
import { EmptyState, PageHeader } from '@/components/page-header';
import { InvoiceMoneyBar } from '@/components/sales/invoice-money-bar';
import { SalesTable } from '@/components/sales/sales-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useMe } from '@/lib/clients';
import { useEInvoiceMap } from '@/lib/einvoicing';
import { useT } from '@/lib/i18n';
import { openNewDrawer } from '@/lib/new-drawer';
import { useOrgId } from '@/lib/org';
import { useSalesTransactions, type SalesRow } from '@/lib/sales';

export const Route = createFileRoute('/sales/')({ component: SalesOverview });

/** How many recent transactions the overview lists before "See all sales". */
export const RECENT_LIMIT = 10;

/** The latest sales transactions, newest first (ties by number, as the lists sort them). */
export function recentSales(rows: SalesRow[], limit = RECENT_LIMIT): SalesRow[] {
  return [...rows]
    .sort((a, b) => b.date.localeCompare(a.date) || (b.number ?? '').localeCompare(a.number ?? ''))
    .slice(0, limit);
}

interface Shortcut {
  label:
    | 'salesPages.salesListCommon.newInvoice'
    | 'salesPages.salesHome.receivePayment'
    | 'salesPages.salesListCommon.newQuote'
    | 'salesPages.salesHome.newCustomer';
  icon: LucideIcon;
  to?: string;
  drawer?: 'customer';
  testId: string;
}

const SHORTCUTS: Shortcut[] = [
  {
    label: 'salesPages.salesListCommon.newInvoice',
    icon: FileText,
    to: '/sales/invoices/new',
    testId: 'shortcut-invoice',
  },
  {
    label: 'salesPages.salesHome.receivePayment',
    icon: Wallet,
    to: '/sales/receive-payment/new',
    testId: 'shortcut-payment',
  },
  {
    label: 'salesPages.salesListCommon.newQuote',
    icon: ReceiptText,
    to: '/sales/quotes/new',
    testId: 'shortcut-quote',
  },
  {
    label: 'salesPages.salesHome.newCustomer',
    icon: UserPlus,
    drawer: 'customer',
    testId: 'shortcut-customer',
  },
];

/** Sales › Overview (document 06 S1): money bar, shortcuts and the latest sales transactions. */
function SalesOverview() {
  const t = useT();
  const orgId = useOrgId();
  const me = useMe();
  const navigate = useNavigate();
  const tx = useSalesTransactions();
  const einvoices = useEInvoiceMap();
  const recent = useMemo(() => recentSales(tx.data ?? []), [tx.data]);
  const isStaff = me.data?.isStaff ?? false;

  return (
    <>
      <PageHeader
        title={t('salesPages.salesHome.title')}
        description={t('salesPages.salesHome.description')}
        actions={
          isStaff ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="gap-1">
                  {t('salesPages.salesListCommon.newTransaction')}{' '}
                  <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <MenuLink to="/sales/invoices/new">{t('salesPages.kind.invoice')}</MenuLink>
                <MenuLink to="/sales/receive-payment/new">{t('salesPages.kind.payment')}</MenuLink>
                <MenuLink to="/sales/quotes/new">{t('salesPages.kind.quote')}</MenuLink>
                <MenuLink to="/sales/credit-notes/new">{t('salesPages.kind.credit_note')}</MenuLink>
                <MenuLink to="/sales/sales-receipts/new">
                  {t('salesPages.kind.sales_receipt')}
                </MenuLink>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null
        }
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : (
        <>
          <InvoiceMoneyBar
            onSelect={(key) => {
              if (!key) return;
              void navigate({
                to: '/sales/invoices',
                search: { status: key === 'overdue' || key === 'not_due' ? key : 'paid' },
              });
            }}
          />
          {isStaff ? (
            <section
              aria-label={t('salesPages.salesHome.shortcuts')}
              className="mb-4"
              data-testid="sales-shortcuts"
            >
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {SHORTCUTS.map((s) =>
                  s.drawer ? (
                    <button
                      key={s.label}
                      type="button"
                      data-testid={s.testId}
                      onClick={() => openNewDrawer({ kind: s.drawer! })}
                      className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3 text-start text-sm font-medium transition hover:border-brand-violet-200 hover:bg-accent hover:text-accent-foreground"
                    >
                      <ShortcutIcon icon={s.icon} />
                      {t(s.label)}
                    </button>
                  ) : (
                    <Link
                      key={s.label}
                      to={s.to as never}
                      data-testid={s.testId}
                      className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3 text-sm font-medium transition hover:border-brand-violet-200 hover:bg-accent hover:text-accent-foreground"
                    >
                      <ShortcutIcon icon={s.icon} />
                      {t(s.label)}
                    </Link>
                  ),
                )}
              </div>
            </section>
          ) : null}
          <section aria-labelledby="recent-sales" className="rounded-lg border bg-card">
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <h2 id="recent-sales" className="text-base font-semibold">
                {t('salesPages.salesHome.recentSales')}
              </h2>
              <Link to="/sales/all" className="flex items-center gap-1 text-sm text-primary">
                {t('salesPages.salesHome.seeAllSales')}{' '}
                <ArrowRight className="rtl:-scale-x-100 size-3.5" />
              </Link>
            </div>
            {tx.isPending ? (
              <p className="px-4 pb-4 text-sm text-muted-foreground">{t('common.loading')}</p>
            ) : tx.isError ? (
              <p className="px-4 pb-4 text-sm text-destructive">{tx.error.message}</p>
            ) : !recent.length ? (
              <EmptyState
                title={t('salesPages.salesListCommon.noSalesYet')}
                hint={
                  isStaff
                    ? t('salesPages.salesHome.emptyStaff')
                    : t('salesPages.salesHome.emptyViewer')
                }
              />
            ) : (
              <SalesTable rows={recent} einvoices={einvoices} />
            )}
          </section>
        </>
      )}
    </>
  );
}

function ShortcutIcon({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span className="grid size-8 place-items-center rounded bg-brand-violet-50">
      <Icon className="size-4 text-primary" />
    </span>
  );
}
